import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import Text from "~/components-shared/Text";
import googleCalendar from "~/assets/i/logos/google-calendar-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, CRUDActionEnum, EventSourceEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import eventsActionCreators from "~/store/events/actions";
import styled, { useTheme } from "styled-components";
import { Card, DateBadge, Dropdown, Grid, Icon, InputToggle, Modal, Picture } from "~/components-shared";
import { useTranslation } from "react-i18next";
import lists from "~/lib/lists";
import useAbacExt from "~/hooks/abac";
import eventSelectors from "~/store/events/selectors";
import authSelectors from "~/store/auth/selectors";
import GoogleEventReadModal from "~/components/GoogleEventReadModal";
import toastsActionCreators from "~/store/toasts/actions";
import { useHistory } from "react-router-dom";
import { resolveToDaysDates } from "~/lib/helpers";
const List = styled.ul`
  flex-grow: 1;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  margin: -${_ref => {
  let {
    theme
  } = _ref;
  return theme.typography.fontStep * 3;
}}rem;
`;
const Item = styled.li`
  position: relative;
  cursor: pointer;
  padding: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.typography.fontStep * 3;
}}rem;
  border-radius: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.shape.borderRadius.primary;
}};

  &:hover {
    background-color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.background.secondary;
}};
  }
`;
const StyledDropdown = styled(Dropdown)`
  position: absolute;
  top: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.typography.fontStep * 3;
}}rem;
  right: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.typography.fontStep * 3;
}}rem;
`;
const StyledPicture = styled(Picture)`
  position: absolute;
  top: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.typography.fontStep * 4;
}}rem;
  left: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.typography.fontStep;
}}rem;
`;
const DailyEventListCard = _ref9 => {
  let {
    day,
    filter,
    onFilter,
    events,
    limit,
    ...rest
  } = _ref9;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const status = useSelector(eventSelectors.getStatus);
  const importStatus = useSelector(eventSelectors.getImportStatus);
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(undefined);
  const history = useHistory();
  let tempEvents = events;
  if (limit) {
    tempEvents = events.slice(0, limit);
  }
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(eventsActionCreators.fetchStarted());
    }
  }, [status, dispatch]);
  const handleClick = event => {
    if (event.source === EventSourceEnum.GOOGLE) {
      setOpen(event);
    } else {
      dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.READ, event.id));
    }
  };
  const handleImportToJoyn = (event, callback) => {
    dispatch(eventsActionCreators.importStarted(EventSourceEnum.GOOGLE, event.id, _ref10 => {
      let {
        data: newEvent
      } = _ref10;
      if (newEvent) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("DailyEventListCard.eventImported", {
            name: newEvent.name
          }),
          timeout: 10000,
          actions: [{
            icon: "external-link",
            onClick: () => history.push(RoutesEnum.EVENT.replace(":eventId", newEvent.id))
          }]
        }));
        dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.READ, newEvent.id));
      } else {
        toastsActionCreators.pushed({
          severity: "danger",
          message: t("DailyEventListCard.importFailed"),
          timeout: 10000
        });
      }
      callback();
    }));
  };
  const handleUpdate = id => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.UPDATE, id));
  };
  const handleDelete = id => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.DELETE, id));
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 0
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true
  }, rest), /*#__PURE__*/React.createElement(List, null, tempEvents.length ? tempEvents.map(event => event.source === EventSourceEnum.JOYN ? /*#__PURE__*/React.createElement(Item, {
    key: event.id,
    onClick: () => handleClick(event)
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 3,
    template: "4.1rem 1fr auto"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, event.googleCalendarSync && /*#__PURE__*/React.createElement(StyledPicture, {
    src: googleCalendar,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("DailyEventListCard.fromGoogleCalendar"),
    width: "1.6rem",
    height: "1.6rem",
    color: "white"
  }), /*#__PURE__*/React.createElement(DateBadge, {
    date: event.dateFrom,
    color: lists.events.types.colors(theme)[event.type],
    s: {
      mt: 3
    },
    responseStatus: event.attendees.find(_ref11 => {
      let {
        self
      } = _ref11;
      return self;
    })?.responseStatus
  })), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: lists.events.types.colors(theme)[event.type],
    fontSize: 3,
    weight: "semiBold",
    display: "block"
  }, lists.events.types.names[event.type]), /*#__PURE__*/React.createElement(Text, {
    element: "h5"
  }, event.name), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    element: "span",
    color: "textSecondary",
    s: {
      mr: 3
    }
  }, resolveToDaysDates(day, event.dateFrom, event.dateTo))), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    scale: 3,
    s: {
      mr: 1
    }
  }), event.attendees.length, " ", /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.secondary.main
  }, "(", event.attendees.filter(attendee => attendee.active).length, ")")))))), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event.createdBy.id) && /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref12 => {
    let {
      isOpen,
      onItemClick
    } = _ref12;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        handleUpdate(event.id);
        onItemClick();
        e.stopPropagation();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        handleDelete(event.id);
        onItemClick();
        e.stopPropagation();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  })) : /*#__PURE__*/React.createElement(Item, {
    key: event.id,
    onClick: () => handleClick(event)
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 3,
    template: "58px 1fr auto"
  }, /*#__PURE__*/React.createElement(DateBadge, {
    date: event.dateFrom,
    color: "#1a73e8",
    s: {
      mt: 3
    }
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "#1a73e8",
    fontSize: 3,
    weight: "semiBold",
    display: "block"
  }, "Google Calendar"), /*#__PURE__*/React.createElement(Text, {
    element: "h5"
  }, event.name), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    element: "span",
    color: "textSecondary",
    s: {
      mr: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    element: "span",
    color: "textSecondary",
    s: {
      mr: 3
    }
  }, resolveToDaysDates(day, event.dateFrom, event.dateTo)))), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    scale: 3,
    s: {
      mr: 1
    }
  }), event.attendees.length))))), /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref13 => {
    let {
      isOpen,
      onItemClick
    } = _ref13;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        handleImportToJoyn(event, onItemClick);
        e.stopPropagation();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: importStatus === StatusStateEnum.LOADING ? "spinner" : "download",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("DailyEventListCard.importToJoyn")));
  }))) : /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    color: "textSecondary",
    s: {
      p: 4
    }
  }, t("EventList.noEvents"))), /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    s: {
      mt: 5
    }
  }, /*#__PURE__*/React.createElement(InputToggle, {
    value: !!filter.memberId,
    label: t("EventList.myEventsOnly"),
    onChange: value => onFilter({
      ...filter,
      memberId: value ? user?.id : undefined
    })
  })), /*#__PURE__*/React.createElement(Modal, {
    open: !!open
  }, /*#__PURE__*/React.createElement(GoogleEventReadModal, {
    event: open,
    onClose: () => setOpen(undefined)
  })));
};
export default DailyEventListCard;